import { ungzip } from "pako";
import { Message } from "./Rounds";
import { useEffect, useState } from "react";
import Round from "./Round";
import Commander from "../../commander";
import classes from "../../styles/History.module.css";
import { getConfig, applyTheme, spliceURLs, validateLang } from "../../utils";
import { useAppSelector } from "../../hooks";
import Spinner from "../Spinner";
import { PublicAPI } from "../../commander";

const getURLParam = (
  param: string | string[],
  defaultValue: null | string = ""
): string | null => {
  const allParams = typeof param === "string" ? [param] : param;

  return allParams.reduce((value, p) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(p) ?? value;
  }, defaultValue);
};

const decodeResponse = (base64: string) => {
  const gzip = atob(base64);
  const gzipDataArray = Uint8Array.from(gzip, (c) => c.charCodeAt(0));
  const data = ungzip(gzipDataArray);
  const str = new TextDecoder().decode(data);

  return JSON.parse(str);
};

const fetchData = async (roundId: string) => {
  const api = window.Elysium.historyApi;
  const environment = getURLParam("environment", "development") as string;
  const version = getURLParam("version", "1");
  const url = spliceURLs(
    api,
    environment,
    `/v${version}/history/round/${roundId}`
  );

  const res = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const data: any = await res.json();

  if (!data.rounds || data.rounds.length === 0) {
    return { error: data.message ?? "Server Error" };
  }
  const round = data.rounds[0];

  const response = JSON.parse(round.response);
  const decodedRound = decodeResponse(response.game.results.round);

  return { ...round, gameRound: decodedRound };
};

const RoundPage: React.FC<{}> = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const roundId = queryParameters.get("roundId");
  const [id, setId] = useState("");
  const [error, setError] = useState("");

  const { gameName } = useAppSelector((state) => state.details);

  // sets the inital color theme if such
  useEffect(() => {
    const setTheme = async () => {
      const cfg = await getConfig();
      applyTheme(cfg.theme);
    };

    setTheme();
  }, [gameName]);

  useEffect(() => {
    // TODO usually the validation of the language is done by the game-manager but since 
    // this page is loaded without it, it is done here as well, which mean there is code
    // duplication. Fix it by doing the validation in the UI or even better create some
    // library for the utils functions
    const language = validateLang(
      getURLParam(["language", "languageId"], "en")
    );
    PublicAPI.setLanguage(language);
  }, []);

  useEffect(() => {
    if (!roundId) return;

    fetchData(roundId)
      .then((r) => {
        if (r.error) {
          setError(r.error);
        } else {
          Commander.addHistoryRound(r);
          setId(r.roundId);
        }
      })
      .catch((e) => {
        setError(e.message ?? "Error");
      });
  }, [roundId]);

  return (
    <div className={classes["round-page"]}>
      <Spinner size={35} visible={!id && !error} />
      <Round id={id} visible={!!id} />
      <Message visible={!!error} type="error" content={error} />
    </div>
  );
};

export default RoundPage;
